import { api } from "../api";
import type { SalesHistoryRow, Order } from "../api";

/* Export dashboard → CSV multi-section (AI-friendly: plain text, tách bằng dòng === TITLE ===
   và dòng trống giữa các section; Excel vẫn mở được, mỗi section dán ra sheet riêng nếu cần).
   1) TONG HOP THEO NGAY — 1 dòng/ngày
   2) THANG YYYY-MM — theo SP của đúng tháng đó (mỗi tháng một section)
   3) THEO SAN PHAM — cả kỳ
   4) DON HANG — từng đơn
   Lấy đủ dữ liệu: phân trang 200 dòng tới cạn (kỳ 1/1→10/10 vẫn đầy đủ). */

const THU = ["Chu nhat", "Thu hai", "Thu ba", "Thu tu", "Thu nam", "Thu sau", "Thu bay"];

function csvCell(v: string | number): string {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function csvSection(lines: (string | number)[][]): string {
  return lines.map((r) => r.map(csvCell).join(",")).join("\n");
}
function isoWeek(d: string): string {
  const dt = new Date(`${d}T12:00:00Z`);
  const t = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()));
  t.setUTCDate(t.getUTCDate() - ((t.getUTCDay() + 6) % 7) + 3);
  const first = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  first.setUTCDate(first.getUTCDate() - ((first.getUTCDay() + 6) % 7) + 3);
  return `${t.getUTCFullYear()}-W${String(1 + Math.round((t.getTime() - first.getTime()) / (7 * 86400000))).padStart(2, "0")}`;
}

async function fetchAllSalesHistory(from: string, to: string): Promise<SalesHistoryRow[]> {
  const out: SalesHistoryRow[] = [];
  for (let offset = 0; ; offset += 200) {
    const page = await api.salesHistory({ from, to, limit: 200, offset });
    out.push(...page.rows);
    if (page.rows.length < 200 || offset > 100_000) break;
  }
  return out;
}
async function fetchAllOrders(from: string, to: string): Promise<Order[]> {
  const out: Order[] = [];
  for (let offset = 0; ; offset += 200) {
    const page = await api.orders({ from, to, limit: 200, offset });
    out.push(...page.orders);
    if (page.orders.length < 200 || offset > 100_000) break;
  }
  return out;
}

function dayOf(r: SalesHistoryRow): string {
  return (r.sold_at || r.sale_date).slice(0, 10);
}

export async function exportDashboardCsv(from: string, to: string): Promise<number> {
  const [histRows, orders] = await Promise.all([fetchAllSalesHistory(from, to), fetchAllOrders(from, to)]);
  const blocks: string[] = [];

  // 1) Tổng hợp theo ngày
  const byDay = new Map<string, { rev: number }>();
  for (const r of histRows) {
    const d = dayOf(r);
    byDay.set(d, { rev: (byDay.get(d)?.rev || 0) + r.revenue });
  }
  const dayOrders = new Map<string, { orders: number; refunds: number }>();
  for (const o of orders) {
    const d = o.receipt_date.slice(0, 10);
    const cur = dayOrders.get(d) || { orders: 0, refunds: 0 };
    if (o.receipt_type === "REFUND") cur.refunds += 1;
    else cur.orders += 1;
    dayOrders.set(d, cur);
  }
  const days = [...new Set([...byDay.keys(), ...dayOrders.keys()])].sort();
  blocks.push("=== TONG HOP THEO NGAY ===");
  blocks.push(csvSection([
    ["Ngay", "Thu", "Tuan ISO", "Thang", "So don", "Don hoan", "Doanh thu", "AOV"],
    ...days.map((d) => {
      const rev = byDay.get(d)?.rev || 0;
      const od = dayOrders.get(d)?.orders || 0;
      return [d, THU[new Date(`${d}T12:00:00Z`).getUTCDay()], isoWeek(d), d.slice(0, 7),
        od, dayOrders.get(d)?.refunds || 0, rev, od > 0 ? Math.round(rev / od) : 0] as (string | number)[];
    }),
  ]));

  // 2) Theo tháng — mỗi tháng một section, theo SP trong tháng đó
  const monthKeys = [...new Set(days.map((d) => d.slice(0, 7)))].sort();
  for (const mk of monthKeys) {
    const monthRows = histRows.filter((r) => dayOf(r).slice(0, 7) === mk);
    const prod = new Map<string, { name: string; sku: string; cat: string; qty: number; rev: number }>();
    for (const r of monthRows) {
      const k = `${r.item_name}|${r.sku || ""}`;
      const cur = prod.get(k) || { name: r.item_name, sku: r.sku || "", cat: r.category || "", qty: 0, rev: 0 };
      cur.qty += r.qty; cur.rev += r.revenue;
      prod.set(k, cur);
    }
    const totalRev = monthRows.reduce((s, r) => s + r.revenue, 0) || 1;
    blocks.push("");
    blocks.push(`=== THANG ${mk} ===`);
    blocks.push(csvSection([
      ["Ten SP", "SKU", "Nhom", "SL ban", "Doanh thu", "Gia TB", "% DT"],
      ...[...prod.values()].sort((a, b) => b.rev - a.rev).map((p) => [
        p.name, p.sku || "-", p.cat || "-", p.qty, p.rev,
        p.qty > 0 ? Math.round(p.rev / p.qty) : 0,
        +(p.rev / totalRev * 100).toFixed(1),
      ] as (string | number)[]),
    ]));
  }

  // 3) Theo sản phẩm cả kỳ
  const prodAll = new Map<string, { name: string; sku: string; cat: string; qty: number; rev: number }>();
  for (const r of histRows) {
    const k = `${r.item_name}|${r.sku || ""}`;
    const cur = prodAll.get(k) || { name: r.item_name, sku: r.sku || "", cat: r.category || "", qty: 0, rev: 0 };
    cur.qty += r.qty; cur.rev += r.revenue;
    prodAll.set(k, cur);
  }
  const totalRevAll = histRows.reduce((s, r) => s + r.revenue, 0) || 1;
  blocks.push("");
  blocks.push("=== THEO SAN PHAM (CA KY) ===");
  blocks.push(csvSection([
    ["Ten SP", "SKU", "Nhom", "SL ban", "Doanh thu", "Gia TB", "% DT"],
    ...[...prodAll.values()].sort((a, b) => b.rev - a.rev).map((p) => [
      p.name, p.sku || "-", p.cat || "-", p.qty, p.rev,
      p.qty > 0 ? Math.round(p.rev / p.qty) : 0,
      +(p.rev / totalRevAll * 100).toFixed(1),
    ] as (string | number)[]),
  ]));

  // 4) Đơn hàng
  blocks.push("");
  blocks.push("=== DON HANG ===");
  blocks.push(csvSection([
    ["So don", "Ngay gio", "Tong tien", "Giam gia", "Thanh toan", "Loai"],
    ...[...orders].sort((a, b) => b.receipt_date.localeCompare(a.receipt_date)).map((o) => [
      o.receipt_number,
      new Date(o.receipt_date).toLocaleString("vi-VN"),
      o.total_money, o.total_discount, o.payment_type, o.receipt_type || "SALE",
    ] as (string | number)[]),
  ]));

  const csv = "\uFEFF" + blocks.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `bao-cao-${from}-${to}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  return histRows.length;
}
